/**
 * Organization page: the people of the signed-in manager's company on Fluxability Accounts.
 * Everything shown comes ready-made from `GET /api/organization/people`; the server decides who may
 * see it and which actions each row offers. Every change is a top-level navigation to a Fluxability
 * Accounts confirm page, which returns here with `fluxaccounts_result` and `action` in the URL. The
 * page passes those to the server once, shows the notice it returns, and removes them from the URL.
 *
 * @module apps/web/src/pages/OrganizationPage
 */

import { memo, useCallback, useEffect, useEffectEvent, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  Building2,
  CircleAlert,
  Loader2,
  Lock,
  MoreHorizontal,
  RefreshCw,
  Search,
  ShieldCheck,
  UserPlus,
  UsersRound,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { UserAvatar } from '@/components/ui/user-avatar';
import { useToast } from '@/hooks/use-toast';
import {
  organizationApi,
  type OrganizationPerson,
  type OrganizationRoster,
  type OrganizationTone,
} from '@/lib/organizationApi';
import { cn } from '@/lib/utils';

/** Delay between the last keystroke and the search request, in milliseconds. */
const SEARCH_DEBOUNCE_MS = 300;

/** Badge classes for each server-chosen tone. */
const toneClasses: Record<OrganizationTone, string> = {
  success: 'border-transparent bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  info: 'border-transparent bg-sky-500/15 text-sky-700 dark:text-sky-300',
  warning: 'border-transparent bg-amber-500/15 text-amber-700 dark:text-amber-300',
  danger: 'border-transparent bg-red-500/15 text-red-700 dark:text-red-300',
  neutral: 'border-transparent bg-muted text-muted-foreground',
};

/** What the page is showing. */
type ViewState =
  | {
      /** First page is loading. */
      kind: 'loading';
    }
  | {
      /** A roster is shown. */
      kind: 'ready';
      /** Loaded people so far, with the latest page's metadata. */
      roster: OrganizationRoster;
      /** A further page is being fetched. */
      loadingMore: boolean;
    }
  | {
      /** The server answered 403: the viewer is not an owner or manager. */
      kind: 'forbidden';
    }
  | {
      /** The server answered 503: the FluxAccounts connection is not configured. */
      kind: 'unconfigured';
    }
  | {
      /** Any other failure. */
      kind: 'error';
      /** Message to show. */
      message: string;
    };

/** Format an RFC 3339 time as a short local date, or a dash. */
function shortDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Organization people page at `/organization`. */
export function OrganizationPage() {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const company = searchParams.get('company');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [state, setState] = useState<ViewState>({ kind: 'loading' });
  const [reloadKey, setReloadKey] = useState(0);
  // Read the return pair once, on first render; it is removed from the URL after the first load.
  const returned = useRef({
    result: searchParams.get('fluxaccounts_result'),
    action: searchParams.get('action'),
  });

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  // Remove the FluxAccounts return pair from the current URL. searchParams is read only for this
  // cleanup and must not restart the people load; company already tracks the URL.
  const clearReturnPair = useEffectEvent(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('fluxaccounts_result');
    next.delete('action');
    setSearchParams(next, { replace: true });
  });

  useEffect(() => {
    const controller = new AbortController();
    const { result, action } = returned.current;
    setState(previous => (previous.kind === 'ready' ? previous : { kind: 'loading' }));
    organizationApi
      .people({ company, query, fluxaccountsResult: result, action }, controller.signal)
      .then(response => {
        if (controller.signal.aborted) return;
        if (result || action) {
          returned.current = { result: null, action: null };
          clearReturnPair();
        }
        if (response.success && response.data) {
          const roster = response.data;
          setState({ kind: 'ready', roster, loadingMore: false });
          if (roster.notice) {
            toast({
              title: roster.notice.title,
              description: roster.notice.message,
              variant: roster.notice.tone === 'danger' ? 'destructive' : undefined,
            });
          }
        } else if (response.status === 403) {
          setState({ kind: 'forbidden' });
        } else if (response.status === 503) {
          setState({ kind: 'unconfigured' });
        } else {
          setState({
            kind: 'error',
            message: response.error?.message || 'The people list could not be loaded.',
          });
        }
      });
    return () => controller.abort();
  }, [company, query, reloadKey, toast]);

  const loadMore = useCallback(() => {
    if (state.kind !== 'ready' || !state.roster.nextCursor || state.loadingMore) return;
    const current = state.roster;
    setState({ kind: 'ready', roster: current, loadingMore: true });
    organizationApi.people({ company, query, cursor: current.nextCursor }).then(response => {
      if (response.success && response.data) {
        const page = response.data;
        setState({
          kind: 'ready',
          roster: { ...page, people: [...current.people, ...page.people] },
          loadingMore: false,
        });
      } else {
        setState({ kind: 'ready', roster: current, loadingMore: false });
        toast({
          title: 'More people could not be loaded',
          description: response.error?.message || 'Try again in a moment.',
          variant: 'destructive',
        });
      }
    });
  }, [company, query, state, toast]);

  const roster = state.kind === 'ready' ? state.roster : null;
  const counts = useMemo(() => {
    const result: Record<string, number> = { all: roster?.people.length ?? 0 };
    for (const person of roster?.people ?? []) result[person.group] = (result[person.group] ?? 0) + 1;
    return result;
  }, [roster]);
  const visible = useMemo(
    () => (roster?.people ?? []).filter(person => filter === 'all' || person.group === filter),
    [roster, filter]
  );

  return (
    <div className="min-h-full px-3 py-4 sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-6xl flex-col gap-5">
        <section className="rounded-md border bg-card/86 p-5 shadow-sm backdrop-blur-xl">
          <Badge variant="outline" className="mb-3 rounded-md">
            <Building2 className="mr-1 h-3.5 w-3.5" />
            Organization
          </Badge>
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
                {roster ? roster.company.name : 'Organization'}
              </h2>
              <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
                Everyone who signs in with your company account
                {roster ? ` (${roster.company.domain})` : ''}. You are viewing as{' '}
                {roster ? roster.viewer.roleLabel.toLowerCase() : 'a manager'}.
              </p>
            </div>
            {roster && (
              <Button onClick={() => window.location.assign(roster.inviteUrl)}>
                <UserPlus className="mr-2 h-4 w-4" />
                Invite people
              </Button>
            )}
          </div>
          <div className="mt-4 grid gap-3 text-sm md:grid-cols-2">
            <div className="flex gap-3 rounded-md border bg-muted/35 p-3 leading-6">
              <ShieldCheck className="mt-1 h-4 w-4 shrink-0" />
              <p>
                {roster?.confirmationNotice ??
                  "For your security, changes to someone's account are confirmed on Fluxability Accounts"}
                . You'll come straight back here afterwards.
              </p>
            </div>
            <div className="flex gap-3 rounded-md border bg-muted/35 p-3 leading-6">
              <UsersRound className="mt-1 h-4 w-4 shrink-0" />
              <p>
                Your organization is who can sign in. Teams are groups you make inside Fluxability to
                plan work together, and they don't change anyone's account.{' '}
                <RouterLink to="/teams" className="font-medium underline underline-offset-4">
                  Go to Teams
                </RouterLink>
              </p>
            </div>
          </div>
        </section>

        {state.kind === 'forbidden' && (
          <StateCard icon={<Lock className="h-5 w-5" />} title="Only organization managers can see this">
            Owners and managers of your company on Fluxability Accounts can see and manage its people
            here. If you need access, ask your organization's owner. Teams still work as usual.
          </StateCard>
        )}

        {state.kind === 'unconfigured' && (
          <StateCard icon={<CircleAlert className="h-5 w-5" />} title="Organization management isn't set up yet">
            This Fluxability server isn't connected to Fluxability Accounts yet, so people can't be
            listed here. Nothing about your account has changed.
          </StateCard>
        )}

        {state.kind === 'error' && (
          <StateCard
            icon={<CircleAlert className="h-5 w-5" />}
            title="The people list didn't load"
            action={
              <Button variant="outline" onClick={() => setReloadKey(key => key + 1)}>
                <RefreshCw className="mr-2 h-4 w-4" />
                Try again
              </Button>
            }
          >
            {state.message}
          </StateCard>
        )}

        {(state.kind === 'loading' || state.kind === 'ready') && (
          <Card className="rounded-md">
            <CardContent className="space-y-4 p-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="relative w-full lg:max-w-sm">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={event => setSearch(event.target.value)}
                    placeholder="Search by name or email"
                    aria-label="Search people"
                    maxLength={100}
                    className="pl-9"
                  />
                </div>
                {roster && (
                  <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by status">
                    {roster.filters.map(chip => (
                      <button
                        key={chip.id}
                        type="button"
                        aria-pressed={filter === chip.id}
                        onClick={() => setFilter(chip.id)}
                        className={cn(
                          'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                          filter === chip.id
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                        )}
                      >
                        {chip.label}
                        <span className="ml-1.5 opacity-70">{counts[chip.id] ?? 0}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {state.kind === 'loading' ? (
                <LoadingRows />
              ) : visible.length === 0 ? (
                <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
                  {query
                    ? `No one matches “${query}”.`
                    : filter !== 'all'
                      ? 'No one in this group yet.'
                      : 'No people yet. Invite people to get started.'}
                </div>
              ) : (
                <PeopleTable people={roster?.people ?? []} filter={filter} />
              )}

              {state.kind === 'ready' && state.roster.nextCursor && (
                <div className="flex justify-center">
                  <Button variant="outline" onClick={loadMore} disabled={state.loadingMore}>
                    {state.loadingMore && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Load more people
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

/** Inputs of a full-width status card. */
interface StateCardProps {
  /** Leading icon. */
  icon: ReactNode;
  /** Heading. */
  title: string;
  /** Explanation. */
  children: ReactNode;
  /** Optional button below the text. */
  action?: ReactNode;
}

/** Card used for the forbidden, unconfigured and error states. */
function StateCard({ icon, title, children, action }: StateCardProps) {
  return (
    <Card className="rounded-md">
      <CardContent className="flex flex-col items-start gap-3 p-6 sm:flex-row">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border bg-muted">
          {icon}
        </span>
        <div className="space-y-2">
          <h3 className="text-base font-semibold">{title}</h3>
          <p className="max-w-2xl text-sm leading-6 text-muted-foreground">{children}</p>
          {action}
        </div>
      </CardContent>
    </Card>
  );
}

/** Placeholder rows shown while the first page loads. */
function LoadingRows() {
  return (
    <div className="space-y-2" aria-busy="true" aria-label="Loading people">
      {[0, 1, 2, 3].map(row => (
        <div key={row} className="h-14 animate-pulse rounded-md bg-muted/60" />
      ))}
    </div>
  );
}

/** Inputs of the people table. */
interface PeopleTableProps {
  /** Every loaded person, unfiltered. */
  people: OrganizationPerson[];
  /** Status group to show (`all` for everyone); other rows stay mounted but hidden. */
  filter: string;
}

/**
 * People table with role and status badges and a row action menu. A status filter only hides
 * rows: each {@link PersonRow} is memoized, so switching filters flips `hidden` on the rows that
 * change instead of unmounting and recreating every row and its menu.
 */
function PeopleTable({ people, filter }: PeopleTableProps) {
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            <th className="px-3 py-2 font-medium">Person</th>
            <th className="px-3 py-2 font-medium">Role</th>
            <th className="px-3 py-2 font-medium">Status</th>
            <th className="hidden px-3 py-2 font-medium md:table-cell">Last active</th>
            <th className="w-12 px-3 py-2">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {people.map(person => (
            <PersonRow
              key={person.id}
              person={person}
              hidden={filter !== 'all' && person.group !== filter}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Inputs of one roster row. */
interface PersonRowProps {
  /** The row's person. */
  person: OrganizationPerson;
  /** Hide the row because the status filter excludes it; it stays mounted. */
  hidden: boolean;
}

/** One person's row; showing or hiding it re-renders only the `<tr>`, not its cells.
 * @component PersonRowView
 */
function PersonRowView({ person, hidden }: PersonRowProps) {
  return (
    <tr hidden={hidden} className="border-t">
      <PersonCells person={person} />
    </tr>
  );
}

/** One person: avatar, name and email, role and status badges, last activity, actions.
 * @component PersonCellsView
 */
function PersonCellsView({
  person,
}: {
  /** The row's person. */
  person: OrganizationPerson;
}) {
  return (
    <>
      <td className="px-3 py-3">
        <div className="flex items-center gap-3">
          <UserAvatar name={person.name} email={person.email} avatarUrl={person.avatarUrl} />
          <div className="min-w-0">
            <div className="font-medium">{person.name}</div>
            <div className="text-xs text-muted-foreground">{person.email}</div>
          </div>
        </div>
      </td>
      <td className="px-3 py-3">
        <Badge variant="outline" className="whitespace-nowrap rounded-md">
          {person.roleLabel}
        </Badge>
      </td>
      <td className="px-3 py-3">
        <Badge className={cn('whitespace-nowrap rounded-md', toneClasses[person.statusTone])}>
          {person.statusLabel}
        </Badge>
      </td>
      <td className="hidden px-3 py-3 text-muted-foreground md:table-cell">
        {person.lastActiveAt
          ? shortDate(person.lastActiveAt)
          : person.invitedAt
            ? `Invited ${shortDate(person.invitedAt)}`
            : '—'}
      </td>
      <td className="px-3 py-3 text-right">
        <RowActions person={person} />
      </td>
    </>
  );
}

/** Memoized {@link PersonCellsView}. */
const PersonCells = memo(PersonCellsView);

/** Memoized {@link PersonRowView}: re-renders only when its person or visibility changes. */
const PersonRow = memo(PersonRowView);

/** Inputs of a row's action menu. */
interface RowActionsProps {
  /** The row's person. */
  person: OrganizationPerson;
}

/** Menu of the actions the server allows on one person; each opens its Fluxability Accounts confirm page. */
function RowActions({ person }: RowActionsProps) {
  if (person.actions.length === 0) return null;
  const safe = person.actions.filter(action => !action.destructive);
  const destructive = person.actions.filter(action => action.destructive);
  /** Leave Fluxability for the confirm page; it returns here when done. */
  const go = (url: string) => window.location.assign(url);
  return (
    // Non-modal: a modal menu locks page scroll and pointer events on <body>, which restyles the
    // whole page (about 1,500 elements, twice) before the menu can paint.
    <DropdownMenu.Root modal={false}>
      <DropdownMenu.Trigger asChild>
        <Button variant="ghost" size="icon" aria-label={`Actions for ${person.name}`}>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={4}
          className="z-50 min-w-56 rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
        >
          <DropdownMenu.Label className="px-2 py-1.5 text-xs text-muted-foreground">
            Confirmed on Fluxability Accounts
          </DropdownMenu.Label>
          {safe.map(action => (
            <DropdownMenu.Item
              key={action.action}
              onSelect={() => go(action.confirmUrl)}
              className="cursor-pointer rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-accent focus:text-accent-foreground"
            >
              {action.label}
            </DropdownMenu.Item>
          ))}
          {safe.length > 0 && destructive.length > 0 && (
            <DropdownMenu.Separator className="my-1 h-px bg-border" />
          )}
          {destructive.map(action => (
            <DropdownMenu.Item
              key={action.action}
              onSelect={() => go(action.confirmUrl)}
              className="cursor-pointer rounded-sm px-2 py-1.5 text-sm text-red-600 outline-none focus:bg-red-500/10 dark:text-red-400"
            >
              {action.label}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
